import { useState, useCallback } from 'react';

/**
 * Custom hook para gerenciar estado e lógica de filtros
 * @param {Object} initialFilters - Filtros iniciais
 * @param {string} storageKey - Chave para localStorage (opcional)
 * @returns {Object} Estado e funções de filtros
 */
export const useFilters = (initialFilters = {}, storageKey = null) => {
  // Tentar recuperar filtros do localStorage se storageKey for fornecida
  const getInitialFilters = () => {
    if (storageKey) {
      const savedFilters = localStorage.getItem(storageKey);
      if (savedFilters) {
        try {
          return JSON.parse(savedFilters);
        } catch (error) {
          // Se o JSON for inválido, usar filtros iniciais
          return initialFilters;
        }
      }
    }
    return initialFilters;
  };

  const [filters, setFilters] = useState(getInitialFilters);

  // Função para atualizar um filtro específico
  const handleFilterChange = useCallback((name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    
    // Salvar no localStorage se storageKey for fornecida
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(newFilters));
    }
  }, [filters, storageKey]);

  // Função para limpar todos os filtros
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
    
    // Limpar do localStorage se storageKey for fornecida
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }, [initialFilters, storageKey]);

  // Função para obter apenas filtros válidos (não vazios)
  const getValidFilters = useCallback(() => {
    const validFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) validFilters[key] = value;
    });
    return validFilters;
  }, [filters]);

  return {
    filters,
    handleFilterChange,
    clearFilters,
    getValidFilters,
    setFilters
  };
}; 